<template>
  <div
    class="broadcast-overlay"
    @click.self="closeTenantBroadcast"
  >
    <div class="broadcast-popup" role="dialog" aria-label="Broadcast">
      <div class="broadcast-header">
        <span>Broadcast</span>
        <button
          type="button"
          aria-label="Close"
          :disabled="broadcastSending"
          @click="closeTenantBroadcast"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="broadcast-body">
        <input
          v-model="broadcastBuildingQuery"
          type="text"
          class="broadcast-filter"
          placeholder="Filter buildings"
        />
        <div class="broadcast-buildings">
          <label
            v-for="building in filteredBroadcastBuildings"
            :key="building.propertyId"
            class="broadcast-building"
          >
            <input
              type="checkbox"
              :value="building.propertyId"
              v-model="broadcastBuildingIds"
              :disabled="broadcastSending"
            />
            <span>{{ broadcastBuildingLabel(building) }}</span>
          </label>
          <p v-if="!filteredBroadcastBuildings.length" class="broadcast-empty">
            No buildings match.
          </p>
        </div>
        <textarea
          v-model="broadcastMessage"
          class="broadcast-message"
          placeholder="Message in markdown"
          :disabled="broadcastSending"
        ></textarea>
        <div class="broadcast-preview-label">Email preview</div>
        <div class="broadcast-preview" v-html="broadcastPreviewHtml"></div>
        <button
          type="button"
          class="broadcast-send"
          :disabled="broadcastSending || !canSendBroadcast"
          @click="sendTenantBroadcast"
        >
          {{ broadcastSending ? 'Sending…' : 'Send' }}
        </button>
        <div class="broadcast-progress" aria-hidden="true">
          <div
            class="broadcast-progress-fill"
            :style="{ width: broadcastPercent + '%' }"
          ></div>
        </div>
        <ul v-if="broadcastFailures.length" class="broadcast-failures">
          <li v-for="(failure, index) in broadcastFailures" :key="index">
            {{ failure }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import { marked } from 'marked';
import { tenantSearchSmsPayload } from '@/utils/tenantSearchSms';
import {
  stripMarkdown,
  broadcastEmailHtml,
  broadcastEmailSubject,
  filterBuildings,
  buildingChoiceLabel,
  currentTenantsForBuildings,
  broadcastJobs,
  broadcastProgress,
} from '@/utils/tenantBroadcast';

export default {
  name: 'TenantBroadcastDialog',
  props: {
    people: { type: Array, default: null },
  },
  emits: ['close'],
  data() {
    return {
      loadedPeople: [],
      broadcastBuildingQuery: '',
      broadcastBuildings: [],
      broadcastBuildingIds: [],
      broadcastMessage: '',
      broadcastSending: false,
      broadcastDone: 0,
      broadcastTotal: 0,
      broadcastFailures: [],
    };
  },
  computed: {
    roster() {
      return Array.isArray(this.people) ? this.people : this.loadedPeople;
    },
    filteredBroadcastBuildings() {
      return filterBuildings(this.broadcastBuildings, this.broadcastBuildingQuery);
    },
    broadcastPreviewHtml() {
      return broadcastEmailHtml(this.broadcastMessage, (markdown) =>
        marked(markdown || ''),
      );
    },
    broadcastPercent() {
      return broadcastProgress(this.broadcastDone, this.broadcastTotal);
    },
    canSendBroadcast() {
      return (
        this.broadcastBuildingIds.length > 0 &&
        stripMarkdown(this.broadcastMessage).length > 0
      );
    },
  },
  mounted() {
    this.loadBuildings();
    this.loadPeople();
  },
  methods: {
    async loadBuildings() {
      try {
        const { data } = await axios.get('/api/properties-editable');
        this.broadcastBuildings = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Error loading buildings:', error);
        this.broadcastFailures = ['Could not load buildings.'];
      }
    },
    async loadPeople() {
      if (Array.isArray(this.people)) return;
      try {
        const { data } = await axios.get('/api/all-people', {
          params: { id: 0, mostRecentActivity: '1970-01-01T00:00:00Z' },
        });
        this.loadedPeople = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Error loading tenants:', error);
        this.broadcastFailures = ['Could not load tenants.'];
      }
    },
    broadcastBuildingLabel(building) {
      const id = Number(building && building.propertyId);
      const selected = (this.broadcastBuildingIds || []).some(
        (value) => Number(value) === id,
      );
      const count = currentTenantsForBuildings(this.roster, [id]).length;
      return buildingChoiceLabel(building && building.address, selected, count);
    },
    closeTenantBroadcast() {
      if (this.broadcastSending) return;
      this.$emit('close');
    },
    async logContact(type, person, message) {
      try {
        await axios.post('/api/log-contact', {
          type,
          person: person.id,
          message,
        });
      } catch (error) {
        console.error(error);
      }
    },
    async noteBroadcastInSystemChat(message) {
      const selected = new Set(
        (this.broadcastBuildingIds || []).map((id) => Number(id)),
      );
      const buildings = filterBuildings(this.broadcastBuildings, '')
        .filter((building) =>
          selected.has(Number(building.propertyId)),
        )
        .map((building) => String(building.address || '').trim())
        .filter(Boolean);
      try {
        await axios.post('/api/tenant-broadcast/notice', {
          buildings,
          message,
        });
      } catch (error) {
        console.error(error);
      }
    },
    async sendTenantBroadcast() {
      if (this.broadcastSending || !this.canSendBroadcast) return;
      const tenants = currentTenantsForBuildings(
        this.roster,
        this.broadcastBuildingIds,
      );
      const jobs = broadcastJobs(tenants);
      this.broadcastFailures = [];
      this.broadcastDone = 0;
      this.broadcastTotal = jobs.length;
      if (!jobs.length) {
        this.broadcastFailures = [
          'No current tenants for the selected buildings.',
        ];
        return;
      }
      const markdown = this.broadcastMessage;
      const smsBody = stripMarkdown(markdown);
      const emailHtml = broadcastEmailHtml(markdown, (source) => marked(source || ''));
      const subject = broadcastEmailSubject(markdown);
      this.broadcastSending = true;
      try {
        await this.noteBroadcastInSystemChat(smsBody);
        for (const job of jobs) {
          try {
            if (job.channel === 'app') {
              await axios.post('/api/tenant-chat/messages', {
                peopleId: job.peopleId,
                message: markdown,
              });
            } else if (job.channel === 'sms') {
              const plan = tenantSearchSmsPayload(job.person, smsBody);
              if (!plan.ok) {
                throw new Error(plan.reason || 'not sent');
              }
              const { data } = await axios.post('/api/sms/send', plan.payload);
              const status = data && data.message && data.message.status;
              if (status === 'pending_opt_in') {
                throw new Error('waiting for YES or START');
              }
              this.logContact(3, job.person, smsBody);
            } else if (job.channel === 'email') {
              await axios.post('/api/send-email', {
                from: 'communications@bigpropertymanagement.com',
                subject,
                body: emailHtml,
                recipients: job.email,
              });
              this.logContact(1, job.person, smsBody);
            }
          } catch (error) {
            const detail =
              (error.response &&
                error.response.data &&
                (error.response.data.error || error.response.data.message)) ||
              error.message ||
              'not sent';
            const channel =
              job.channel === 'app' ? 'App' : job.channel === 'sms' ? 'SMS' : 'Email';
            this.broadcastFailures.push(`${channel} to ${job.name} failed: ${detail}`);
          } finally {
            this.broadcastDone += 1;
          }
        }
      } finally {
        this.broadcastSending = false;
      }
    },
  },
};
</script>

<style scoped>
.broadcast-overlay {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  padding: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.45);
  z-index: 3000;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.broadcast-popup {
  width: 100%;
  height: 100%;
  max-height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #333;
  border-radius: 8px;
}

.broadcast-header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #6e5875;
  color: #fff;
  font-weight: 600;
}

.broadcast-header button {
  border: none;
  background: transparent;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
}

.broadcast-header button:disabled {
  opacity: 0.35;
  cursor: default;
}

.broadcast-body {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 12px;
  box-sizing: border-box;
}

.broadcast-filter,
.broadcast-message {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-size: 16px;
}

.broadcast-buildings {
  flex: 1 1 0;
  min-width: 0;
  min-height: 18vh;
  overflow-x: hidden;
  overflow-y: auto;
  margin: 8px 0;
  border: 1px solid #ddd;
  padding: 6px;
  box-sizing: border-box;
}

.broadcast-building {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin: 4px 0;
  min-width: 0;
}

.broadcast-building span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.broadcast-message {
  flex: 0 0 auto;
  height: 25vh;
  margin-top: 8px;
  resize: none;
}

.broadcast-preview-label {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
}

.broadcast-preview {
  flex: 0 0 auto;
  min-width: 0;
  border: 1px solid #ddd;
  padding: 8px;
  height: 18vh;
  overflow-x: hidden;
  overflow-y: auto;
  overflow-wrap: anywhere;
  box-sizing: border-box;
  background: #fafafa;
  font-size: 13px;
}

.broadcast-send {
  margin-top: 8px;
}

.broadcast-progress {
  margin-top: 10px;
  height: 10px;
  background: #eee;
  border-radius: 5px;
  overflow: hidden;
}

.broadcast-progress-fill {
  height: 100%;
  width: 0;
  background: #2e7d32;
}

.broadcast-failures {
  margin: 8px 0 0;
  padding-left: 18px;
  color: #b71c1c;
  font-size: 13px;
}

.broadcast-empty {
  margin: 4px 0;
  color: #777;
}
</style>
